import { Component, DestroyRef, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef, MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { AppBaseComponent } from '@shared/components/app-component-base';
import { TestService } from '@core/services/test/test.service';
import { combineLatest } from 'rxjs';
import { AppConstants } from '@core/constants';
import { ApiService } from '@core/services/api.service';
import { AthleteDto } from '@core/services/athletes-management/athletes-dto';
import { String } from 'typescript-string-operations';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { OptimizationOptionDialogComponent } from '../optimization-option-dialog/optimization-option-dialog.component';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-simulate-event-dialog',
  templateUrl: './simulate-event-dialog.component.html',
})
export class SimulateEventDialogComponent extends AppBaseComponent {
  public detailForm: UntypedFormGroup = {} as UntypedFormGroup;
  public athletesOptions: Array<any> = [];
  public athletesFiltered: Array<any> = [];
  public testOptions: Array<any> = [];
  public showActOxydPhos: boolean = true;
  public glyOptions: Array<any> = [];
  public actOxydPhosOptions: Array<any> = [];
  public modelTypes: Array<any> = AppConstants.MODEL_TYPES;
  public event: any;
  public sport: string = '';
  public searchAthleteCtrl: UntypedFormControl = new UntypedFormControl('');

  constructor(
    public dialogRef: MatDialogRef<SimulateEventDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private testService: TestService,
    private readonly formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    private apiService: ApiService,
    private readonly snackBar: MatSnackBar,
    private destroyRef: DestroyRef,
  ) {
    super();
  }

  public onInitPage(): void {
    this.initForm();
    this.initOptionAndValue();
    this.searchAthleteCtrl.valueChanges.pipe(
      debounceTime(450),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this._applyFilter();
    });
  }

  private initOptionAndValue(): void {
    this.getEvent(this.data.id);
    combineLatest([this.apiService.get('athletes/')])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
      ([resAthletes]: any): void => {
        let coachId = this.auth.id;
        let athletes;

        if (this.sessionService.rights.athletes_list_all) {
          athletes = resAthletes?.results;
        } else {
          athletes = resAthletes?.results.filter((a: AthleteDto) =>
            a?.coaches?.find((c): boolean => c.id === coachId)
          );
        }

        this.athletesOptions = athletes;
        this._setupFilter();
      }
    );
  }

  private _setupFilter(): void {
    this.athletesFiltered = this.athletesOptions.slice();
    this._applyFilter();
  }

  private _applyFilter(): void {
    let search = this.searchAthleteCtrl.value;
    if (!search) {
      this.athletesFiltered = this.athletesOptions.slice();
    } else {
      search = search.toLowerCase();

      this.athletesFiltered = this.athletesOptions.filter((field: any) => (field.first_name + ' ' + field.last_name)
        .toLowerCase().includes(search));
    }
  }

  public getEvent(id: number): void {
    const api = 'events/?id=' + id;
    this.apiService.get(api)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: any): void => {
      if(response.results.length){
        this.event = response.results[0];
        this.sport = response.results[0].sport.name;
      } else {
        this.event = null;
      }
    }, error => {
      this.snackBar.open(error, 'OK', AppConstants.TOAST_CONFIG.ERROR);
    });
  }

  public getTestsBySelectedAthlete(athleteId: number): void {
    this.testService.getByAthleteAndSport(athleteId, this.data.sport.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res): void => {
        this.testOptions = res.results;
      });
  }

  public cancel(): void {
    this.dialogRef.close(false);
  }

  public save(): void {
    if (!this.detailForm.valid) {
      this.detailForm.markAllAsTouched();
    } else {
      this.openOptimizationOptionsModal();
    }
  }

  public modelOptionChanged(modelId: number): void {
    if (modelId == 1) {
      this.glyOptions = this.constant.MODEL_1_SUBTYPES;
      this.showActOxydPhos = false;
    } else if (modelId == 2) {
      this.glyOptions = this.constant.MODEL_2_SUBTYPES;
      this.showActOxydPhos = true;
      this.actOxydPhosOptions = this.constant.MODEL_2_SUBTYPES_2;
    } else {
      this.glyOptions = this.constant.MODEL_3_SUBTYPES;
      this.showActOxydPhos = true;
      this.actOxydPhosOptions = this.constant.MODEL_3_SUBTYPES_2;
    }
  }

  public openOptimizationOptionsModal(): void {
    const dialogRef: MatDialogRef<OptimizationOptionDialogComponent> = this.dialog.open(OptimizationOptionDialogComponent, {
      width: '600px',
      panelClass: 'general-dialog',
      data: {
        type: this.constant.TEST_TYPE_CHARS.result,
      },
    });
    dialogRef.afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result): void => {
      if (result.action !== 'confirm') {
        return;
      }
      this.insert(this.prepareDataForSave(), result);
    });
  }

  public checkTestType(type: string): string {
    switch (type) {
      case 'map':
        return 'Critical Power';
      case 'lactate':
        return 'Lactate';
      case 'newtype':
        return 'PPD';
      case 'manual':
        return 'Manual';
      case 'virtual':
        return 'Virtual';
      default:
        return type;
    }
  }

  public prepareDataForSave() {
    const preparedData = {
      event: this.event.id,
      coach: this.auth.id,
      organization: this.auth.organization.id,
      ...this.detailForm.value,
    };
    return preparedData;
  }

  public pcrMlo2OptionChanged(): void {
    const pcrMlo2Control = this.detailForm.get('pcr_mlo2');
    if(pcrMlo2Control?.value && !isNaN(parseFloat(pcrMlo2Control?.value))) {
      pcrMlo2Control?.value.setValue(parseFloat(pcrMlo2Control?.value));
      const watt_o2_equivalent = 12.5;
      pcrMlo2Control?.value.setValue(1 / pcrMlo2Control?.value);
      this.detailForm.get('joule_pcreq')?.value.setValue(pcrMlo2Control?.value / (watt_o2_equivalent / 60));
    }
  }

  public insert(model: any, data: any): void {
    let param_array = [];
    let params: string = '';
    if (data.productId) {
      param_array.push('product=' + data.productId);
    }
    if (data.planId) {
      param_array.push('plan=' + data.planId);
    }
    params = param_array.join('&');
    const p: string =  params ? '?' + params : '';
    const api: string = String.Format('results/{0}', p);
    this.apiService.post(api, model)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: Object): void => {
      this.hideLoading();
      this.snackBar.open('Item created successfully.', 'OK', this.constant.TOAST_CONFIG.SUCCESS);
      this.dialogRef.close(response);
    }, (error): void => {
      this.snackBar.open(error, 'OK', this.constant.TOAST_CONFIG.ERROR);
    });
  }

  public showOptions(event:MatCheckboxChange): void {
    if(event.checked){
      this.detailForm.get('flag_forceDGATP')?.setValue(1);
      return;
    }
    this.detailForm.get('flag_forceDGATP')?.setValue(0);
  }

  public initForm(): void {
    const eventAthleteDataControlsConfig = {
      description: new UntypedFormControl('', Validators.required),
      athlete: new UntypedFormControl('', Validators.required),
      test: new UntypedFormControl('', Validators.required),
      flag_forceDGATP: new UntypedFormControl(''),
      example: new UntypedFormControl(''),
      mod_glycPME: new UntypedFormControl(''),
      act_oxyd_phos: new UntypedFormControl(''),
      stepsize: new UntypedFormControl(''),
      relerr: new UntypedFormControl(''),
      abserr: new UntypedFormControl(''),
      atp_rest: new UntypedFormControl(''),
      adp_rest: new UntypedFormControl(''),
      pcr_rest: new UntypedFormControl(''),
      cr_rest: new UntypedFormControl(''),
      pi_rest: new UntypedFormControl(''),
      amp_rest: new UntypedFormControl(''),
      CLaM_rest: new UntypedFormControl(''),
      CLaB_rest: new UntypedFormControl(''),
      pH_rest: new UntypedFormControl(''),
      conz_F6MP_rest: new UntypedFormControl(''),
      conz_PEP_rest: new UntypedFormControl(''),
      vo2_rest: new UntypedFormControl(''),
      vo2_passive_tissue: new UntypedFormControl(''),
      vent_est_slope: new UntypedFormControl(''),
      vent_est_offset: new UntypedFormControl(''),
      vo2_vent_slope: new UntypedFormControl(''),
      vo2_vent_offset: new UntypedFormControl(''),
      base_pmf: new UntypedFormControl(''),
      dGFakt_Mod1: new UntypedFormControl(''),
      dGFakt_Mod2: new UntypedFormControl(''),
      dGFakt: new UntypedFormControl(''),
      DGOX: new UntypedFormControl(''),
      evo2: new UntypedFormControl(''),
      fakt_vssPME: new UntypedFormControl(''),
      free_CArest: new UntypedFormControl(''),
      joule_pcreq: new UntypedFormControl(''),
      kh_F6MP: new UntypedFormControl(''),
      konv_lapcr: new UntypedFormControl(''),
      pcr_mlo2: new UntypedFormControl(''),
      konv_o2pcr: new UntypedFormControl(''),
      ks_F6MPADP: new UntypedFormControl(''),
      ks_F6MPadpCA: new UntypedFormControl(''),
      ks_F6MPamp: new UntypedFormControl(''),
      ks_F6MPampCOOP: new UntypedFormControl(''),
      ks_F6MPCa: new UntypedFormControl(''),
      ks_lapH: new UntypedFormControl(''),
      ks_oxpi: new UntypedFormControl(''),
      ks_pfkF6MP: new UntypedFormControl(''),
      ks_piPME: new UntypedFormControl(''),
      ks_vlaresADP: new UntypedFormControl(''),
      ks_vlaresCLa: new UntypedFormControl(''),
      ks_vo2_Mod1: new UntypedFormControl(''),
      ks_vo2: new UntypedFormControl(''),
      ks_vo2ADP: new UntypedFormControl(''),
      ks_vPMEres: new UntypedFormControl(''),
      ks2_laADP: new UntypedFormControl(''),
      ks2_laAMP: new UntypedFormControl(''),
      ks2_pi: new UntypedFormControl(''),
      ks2_piPME: new UntypedFormControl(''),
      laexp_pi: new UntypedFormControl(''),
      m2: new UntypedFormControl(''),
      m3: new UntypedFormControl(''),
      pH_mit: new UntypedFormControl(''),
      phy_const: new UntypedFormControl(''),
      reduce_alpha1mV: new UntypedFormControl(''),
      vlaresmax: new UntypedFormControl(''),
      vpme_resmax: new UntypedFormControl(''),
      zk_vo2: new UntypedFormControl(''),
      zk_vo2mod3: new UntypedFormControl(''),
      vo2max_const: new UntypedFormControl(''),
      force_kjlow1: new UntypedFormControl(''),
      force_kjlow0: new UntypedFormControl(''),
      temp: new UntypedFormControl(''),
      slyke: new UntypedFormControl(''),
      slyke1: new UntypedFormControl(''),
      kel_ox: new UntypedFormControl(''),
    };
    this.detailForm = this.formBuilder.group(eventAthleteDataControlsConfig);
  }
}
